/**
 * Focus-recovery decision shared by the two campaign browsers (acquisition Campaigns and
 * operations Promotions). It is a pure function so the one case a component test cannot
 * drive deterministically is still pinned: a PASSIVE re-render must recover nothing.
 *
 * The browsers move keyboard focus back to a real control when a header control tears
 * down the row it lives in (otherwise focus falls to <body> and the next Tab restarts at
 * the top of the page, WCAG 2.4.3). But that recovery must fire only on a USER action,
 * never on a passive re-render: the Campaigns browser ticks every second (useNow), and a
 * lone running campaign can expire out of the Running tab while the owner is just
 * watching, emptying the view with focus already on <body>. Grabbing focus (and scrolling
 * the tab into view) on that tick would be the bug. Each triggering handler therefore sets
 * a flag; this function reads those flags and reports what the layout effect should do.
 */
export type FocusRecovery =
  /** clearSearch (Escape / the chip dismiss): the field always unmounts. Focus the search
   *  toggle if it survived, else the open tab (the term was the only match). */
  | 'toggle-or-tab'
  /** The inline clear or the "All"/category chip, which removed itself by emptying the
   *  view: focus the open tab. */
  | 'tab'
  /** Nothing a user did unmounted a focused control: leave focus where it is. */
  | 'none';

export type FocusFlags = {
  /** clearSearch ran this commit. */
  afterClear: boolean;
  /** The inline clear or a category chip ran this commit. */
  afterNarrow: boolean;
  /** The view went from non-empty to empty on this commit. */
  becameEmpty: boolean;
};

export const focusRecovery = ({ afterClear, afterNarrow, becameEmpty }: FocusFlags): FocusRecovery => {
  if (afterClear) return 'toggle-or-tab';
  // becameEmpty is the precise test that the chip/field removed itself. Without it a
  // narrowing that leaves the row up would still recover focus, and a Safari mouse click
  // (which does not focus a button) would then look like lost focus and get pulled away.
  if (afterNarrow && becameEmpty) return 'tab';
  // No flag set: a passive re-render (e.g. the per-second tick expiring a campaign out of
  // the view). Recover nothing.
  return 'none';
};
